import ChevronDown from "@/assets/icons/ChevronDown";
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectTrigger,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { getValueFromStorage, saveToStorage } from "@/utils/secureStore";
import { t, tx } from "@transifex/native";
import { T, useLanguages } from "@transifex/react";
import { useEffect, useState } from "react";

interface LanguageProps {
  code: string;
  name: string;
}

tx.init({
  token: process.env.EXPO_PUBLIC_TRANSIFEX_TOKEN,
});

export default function LanguagePicker() {
  const languages = useLanguages();
  const [requestStatus, setRequestStatus] = useState<string>("");
  const [locationCode, setLocationCode] = useState("en");

  const timeOutValue = tx.getCurrentLocale() ? 1000 : 3000;

  let selectedLanguage = languages.find((lng: LanguageProps) => lng.code === locationCode);

  if (selectedLanguage) {
    selectedLanguage = selectedLanguage.name;
  } else {
    selectedLanguage = "English";
  }

  const handleLanguageSelection = (selectedLanguage: string): void => {
    setRequestStatus("idle");
    setTimeout(() => {
      tx.setCurrentLocale(selectedLanguage);
      setLocationCode(selectedLanguage);
      saveToStorage("tx-locale", selectedLanguage)
        .then(() => {
          setRequestStatus("success");
        })
        .catch(() => {
          setRequestStatus("error");
        });
    }, timeOutValue);
  };

  useEffect((): void => {
    getValueFromStorage("tx-locale").then((locale: string | null): void => {
      if (locale) {
        tx.setCurrentLocale(locale);
        setLocationCode(locale);
      }
    });
  }, []);

  if (requestStatus === "loading" || requestStatus === "idle") {
    return <Spinner color={"var(--color-primary-900)"} size="large" />;
  }

  return (
    <VStack className="gap-[10px]">
      <Text className="font-[600] leading-[20px] text-[14px] text-typography-900">
        <T _str="Language Selector" />
      </Text>
      <Select className="bg-background-100" onValueChange={handleLanguageSelection}>
        <SelectTrigger className="rounded-[6px] border-outline-300 gap-[8px] px-[16px] py-[8px]" variant="outline" size="xl">
          <SelectInput
            placeholder={languages.length === 0 ? t("Loading") : t("Select option")}
            value={selectedLanguage}
            className="font-[400] leading-[22px] text-[16px] text-typography-900"
          />
          <SelectIcon className="fill-typography-900" as={ChevronDown} />
        </SelectTrigger>
        <SelectPortal>
          <SelectBackdrop />
          <SelectContent>
            <SelectDragIndicatorWrapper>
              <SelectDragIndicator />
            </SelectDragIndicatorWrapper>
            {languages.map((lng: LanguageProps) => (
              <SelectItem key={lng.code} label={lng.name} value={lng.code} />
            ))}
          </SelectContent>
        </SelectPortal>
      </Select>
    </VStack>
  );
}
